// src/utils/animationUtils.js
// Framer Motion 애니메이션 variants 및 유틸리티

// 기기별 애니메이션 최적화 감지
export const getAnimationConfig = () => {
    // prefers-reduced-motion 감지
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // 터치 기기 감지
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // 저사양 기기 감지 (간단한 휴리스틱)
    const isLowEndDevice = navigator.hardwareConcurrency <= 2 ||
        navigator.deviceMemory <= 2 ||
        /Android.*Chrome\/[0-5][0-9]/.test(navigator.userAgent);

    return {
        prefersReducedMotion,
        isTouchDevice,
        isLowEndDevice,
        // 애니메이션 강도 조정
        duration: prefersReducedMotion ? 0.1 : (isLowEndDevice ? 0.2 : 0.3),
        springStiffness: prefersReducedMotion ? 1000 : (isLowEndDevice ? 200 : 300),
        springDamping: prefersReducedMotion ? 50 : (isLowEndDevice ? 25 : 30)
    };
};

// 모달 애니메이션 variants
export const modalVariants = {
    hidden: {
        opacity: 0,
        scale: 0.95,
        y: 20
    },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: getAnimationConfig().springStiffness,
            damping: getAnimationConfig().springDamping,
            duration: getAnimationConfig().duration
        }
    },
    exit: {
        opacity: 0,
        scale: 0.95,
        y: 20,
        transition: {
            duration: getAnimationConfig().duration * 0.7
        }
    }
};

// 오버레이 애니메이션 variants
export const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: getAnimationConfig().duration }
    },
    exit: {
        opacity: 0,
        transition: { duration: getAnimationConfig().duration * 0.7 }
    }
};

// 사이드바 애니메이션 variants
export const sidebarVariants = {
    hidden: {
        x: '-100%',
        opacity: 0
    },
    visible: {
        x: 0,
        opacity: 1,
        transition: {
            type: 'spring',
            stiffness: getAnimationConfig().springStiffness,
            damping: getAnimationConfig().springDamping
        }
    },
    exit: {
        x: '-100%',
        opacity: 0,
        transition: { duration: getAnimationConfig().duration }
    }
};

// 리스트 컨테이너 애니메이션 variants
export const listContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: getAnimationConfig().prefersReducedMotion ? 0 : 0.1,
            delayChildren: 0.1
        }
    }
};

// 리스트 아이템 애니메이션 variants
export const listItemVariants = {
    hidden: {
        opacity: 0,
        y: 20,
        scale: 0.95
    },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            type: 'spring',
            stiffness: getAnimationConfig().springStiffness,
            damping: getAnimationConfig().springDamping
        }
    }
};

// 버튼 애니메이션 variants
export const buttonVariants = {
    rest: { scale: 1 },
    hover: {
        scale: getAnimationConfig().isTouchDevice ? 1 : 1.05,
        transition: { duration: 0.2 }
    },
    tap: {
        scale: 0.95,
        transition: { duration: 0.1 }
    }
};

// 플로팅 버튼 애니메이션 variants
export const floatingButtonVariants = {
    hidden: {
        scale: 0,
        opacity: 0,
        y: 20
    },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: getAnimationConfig().springStiffness,
            damping: getAnimationConfig().springDamping
        }
    },
    exit: {
        scale: 0,
        opacity: 0,
        y: 20,
        transition: { duration: getAnimationConfig().duration }
    }
};

// 페이지 전환 애니메이션 variants
export const pageVariants = {
    initial: {
        opacity: 0,
        y: 20
    },
    in: {
        opacity: 1,
        y: 0,
        transition: {
            duration: getAnimationConfig().duration * 1.5,
            ease: 'easeOut'
        }
    },
    out: {
        opacity: 0,
        y: -20,
        transition: {
            duration: getAnimationConfig().duration,
            ease: 'easeIn'
        }
    }
};

// 로딩 스피너 애니메이션 variants
export const spinnerVariants = {
    animate: {
        rotate: 360,
        transition: {
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
        }
    }
};

// 하이라이트 애니메이션 (새 아이템 강조)
export const highlightVariants = {
    initial: {
        scale: 1,
        boxShadow: '0 0 0 0 rgba(59, 130, 246, 0.5)'
    },
    highlight: {
        scale: 1.02,
        boxShadow: '0 0 0 8px rgba(59, 130, 246, 0.1)',
        transition: {
            duration: 0.3,
            repeat: 2,
            repeatType: 'reverse'
        }
    }
};

// 모바일 bottom sheet 스타일 variants
export const bottomSheetVariants = {
    hidden: {
        y: '100%',
        opacity: 0
    },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            type: 'spring',
            stiffness: getAnimationConfig().springStiffness,
            damping: getAnimationConfig().springDamping
        }
    },
    exit: {
        y: '100%',
        opacity: 0,
        transition: { duration: getAnimationConfig().duration }
    }
};

// 애니메이션 설정을 동적으로 가져오는 함수
export const getModalVariants = () => {
    // 모든 기기에서 중앙 팝업 스타일 사용
    return modalVariants;
};

// 애니메이션 지연 시간 계산
export const getStaggerDelay = (index, baseDelay = 0.1) => {
    const config = getAnimationConfig();
    return config.prefersReducedMotion ? 0 : index * baseDelay;
};
